import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { ThemeProvider } from "styled-components";
import { AboutUsPage } from "./aboutus/AboutUsPage";
import RevokeLicensePage from "./admin/RevokeLicensesPage";
import VerifyArtistsPage from "./admin/VerifyArtistsPage";
import ArticleOptionsPage from "./articles/ArticleOptionsPage";
import ArticlePage from "./articles/ArticlePage";
import HowItWorksPage from "./articles/HowItWorksPage";
import GlobalStyle from "./components/GlobalStyleType";
import { darkTheme, lightTheme } from "./components/Themes";
import { ConnectWalletPage } from "./connectwallet/ConnectWalletPage";
import { Homepage } from "./homepage/Homepage";
import { useDarkMode } from "./hooks/useDarkMode";
import { IssueLicensePage } from "./licensehub/IssueLicensePage";
import { LicenseHubInfo } from "./licensehub/LicenseHubInfo";
import { LicenseLoadingPage } from "./licensehub/LicenseLoadingPage";
import { SubscriberCountPage } from "./licensehub/SubscriberCountPage";
import { TermsAndConditionsPage } from "./licensehub/TermsAndConditionsPage";
import { LicenseOwners } from "./managelicenses/views/LicenseOwners";
import { LicenseTypeModal } from "./managelicenses/views/LicenseTypeModal";
import { ManageLicensesModal } from "./managelicenses/views/ManageLicensesModal";
import { MarketPlacePage } from "./marketplace/MarketPlacePage";
import { CreateNFTOptionPage } from "./nftengine/CreateNFTOptionPage";
import { CreateNFTPage } from "./nftengine/CreateNFTPage";
import { DeployCollectibleNFTPage } from "./nftengine/DeployCollectibleNFTPage";
import { ListOptionsPage } from "./nftengine/ListOptionsPage";
import { MasterCopyTermsConditionsPage } from "./nftengine/MasterCopyTermsConditonsPage";
import { MintingProgressPage } from "./nftengine/MintingProgressPage";
import { SetLaunchPage } from "./nftengine/SetLaunchPage";
import { UploadFilePage } from "./nftengine/UploadFilePage";
import { UploadSongDetailsPage } from "./nftengine/UploadSongDetailsPage";
import PendingVerificationPage from "./pendingverification/PendingVerificationPage";
import { PrivacyPolicyPage } from "./privacy/PrivacyPolicyPage";
import { AuthProvider } from "./providers/AuthProvider";
import AdminRoute from "./routes/AdminRoute";
import ProtectedRoute from "./routes/ProtectedRoute";
import VerifiedUserRoute from "./routes/VerifiedUserRoute";
import SongDetailPage from "./songdetail/SongDetailPage";
import { NewArtistInfoPage } from "./userprofile/NewArtistInfoPage";
import UserProfileOutlet from "./userprofile/UserProfileOutlet";
import { UserProfilePage } from "./userprofile/UserProfilePage";

function App() {
  const [theme, themeLoaded, themeToggler] = useDarkMode();

  return (
    <>
      <AuthProvider>
        {themeLoaded && (
          <ThemeProvider theme={theme === "light" ? lightTheme : darkTheme}>
            <GlobalStyle />

            <Router>
              <Routes>
                <Route
                  path="/"
                  element={
                    <Homepage
                      theme={theme as string}
                      themeToggler={themeToggler}
                    />
                  }
                />
                <Route
                  path="/aboutus"
                  element={
                    <AboutUsPage
                      theme={theme as string}
                      toggleTheme={themeToggler}
                    />
                  }
                />
                <Route
                  path="/howitworks"
                  element={
                    <HowItWorksPage
                      theme={theme as string}
                      toggleTheme={themeToggler}
                    />
                  }
                />

                <Route
                  path="/connect_wallet"
                  element={
                    <ConnectWalletPage
                      theme={theme as string}
                      toggleTheme={themeToggler}
                    />
                  }
                />

                <Route
                  path="/pending_verification"
                  element={
                    <PendingVerificationPage
                      theme={theme as string}
                      toggleTheme={themeToggler}
                    />
                  }
                />
                <Route
                  path="/articles"
                  element={
                    <ArticleOptionsPage
                      theme={theme as string}
                      toggleTheme={themeToggler}
                    />
                  }
                >
                  <Route path=":id" element={<ArticlePage />} />
                </Route>
                <Route
                  path="/explore"
                  element={
                    <MarketPlacePage
                      theme={theme as string}
                      toggleTheme={themeToggler}
                    />
                  }
                ></Route>
                <Route
                  path="song/:songid"
                  element={
                    <SongDetailPage
                      theme={theme as string}
                      toggleTheme={themeToggler}
                      isCollection={false}
                    />
                  }
                >
                  <Route
                    path="manage_licenses"
                    element={<ManageLicensesModal theme={theme as string} />}
                  />
                  <Route
                    path="manage_licenses/license_owners"
                    element={<LicenseOwners theme={theme as string} />}
                  />
                  <Route
                    path="manage_licenses/license_types"
                    element={<LicenseTypeModal />}
                  />
                </Route>

                <Route
                  path="collection/:songid"
                  element={
                    <SongDetailPage
                      theme={theme as string}
                      toggleTheme={themeToggler}
                      isCollection={true}
                    />
                  }
                />

                {/*TODO: Going to keep this page because we might want to use this page to verify artist's email
                if we decide not to use this page, please remove this.
                 */}
                {/* <Route
                  path="/signup_verification"
                  element={<SignupVerificationPage />}
                /> */}
                <Route
                  path="/artist_info"
                  element={
                    <ProtectedRoute>
                      <NewArtistInfoPage theme={theme as string} />
                    </ProtectedRoute>
                  }
                />
                <Route path="/user" element={<UserProfileOutlet />}>
                  <Route
                    path=":id"
                    element={
                      <UserProfilePage
                        theme={theme as string}
                        toggleTheme={themeToggler}
                      />
                    }
                  />
                </Route>

                <Route
                  path="/upload_file"
                  element={
                    <ProtectedRoute>
                      <VerifiedUserRoute>
                        <UploadFilePage
                          theme={theme as string}
                          toggleTheme={themeToggler as () => void}
                        />
                      </VerifiedUserRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/create_nft"
                  element={
                    <ProtectedRoute>
                      <VerifiedUserRoute>
                        <CreateNFTPage
                          theme={theme as string}
                          toggleTheme={themeToggler as () => void}
                        />
                      </VerifiedUserRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/choose_nft_option"
                  element={
                    <ProtectedRoute>
                      <VerifiedUserRoute>
                        <CreateNFTOptionPage
                          theme={theme as string}
                          toggleTheme={themeToggler as () => void}
                        />
                      </VerifiedUserRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/upload_song_details"
                  element={
                    <ProtectedRoute>
                      <VerifiedUserRoute>
                        <UploadSongDetailsPage
                          theme={theme as string}
                          toggleTheme={themeToggler as () => void}
                        />
                      </VerifiedUserRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/list_options"
                  element={
                    <ProtectedRoute>
                      <VerifiedUserRoute>
                        <ListOptionsPage
                          theme={theme as string}
                          toggleTheme={themeToggler as () => void}
                        />
                      </VerifiedUserRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/minting_progress"
                  element={
                    <ProtectedRoute>
                      <VerifiedUserRoute>
                        <MintingProgressPage
                          theme={theme as string}
                          toggleTheme={themeToggler as () => void}
                        />
                      </VerifiedUserRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/collectible_deploy"
                  element={
                    <ProtectedRoute>
                      <VerifiedUserRoute>
                        <DeployCollectibleNFTPage
                          theme={theme as string}
                          toggleTheme={themeToggler as () => void}
                        />
                      </VerifiedUserRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/set_launch_page"
                  element={
                    <ProtectedRoute>
                      <VerifiedUserRoute>
                        <SetLaunchPage
                          theme={theme as string}
                          toggleTheme={themeToggler as () => void}
                        />
                      </VerifiedUserRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/license_hub_info"
                  element={
                    <ProtectedRoute>
                      <VerifiedUserRoute>
                        <LicenseHubInfo
                          theme={theme as string}
                          toggleTheme={themeToggler}
                        />
                      </VerifiedUserRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/issue_license"
                  element={
                    <ProtectedRoute>
                      <VerifiedUserRoute>
                        <IssueLicensePage
                          theme={theme as string}
                          toggleTheme={themeToggler}
                        />
                      </VerifiedUserRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/license_loading_page"
                  element={
                    <ProtectedRoute>
                      <VerifiedUserRoute>
                        <LicenseLoadingPage theme={theme as string} />
                      </VerifiedUserRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/subscriber_count_pricing"
                  element={
                    <ProtectedRoute>
                      <VerifiedUserRoute>
                        <SubscriberCountPage
                          theme={theme as string}
                          toggleTheme={themeToggler}
                        />
                      </VerifiedUserRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/terms_and_conditions"
                  element={
                    <ProtectedRoute>
                      <VerifiedUserRoute>
                        <TermsAndConditionsPage theme={theme as string} />
                      </VerifiedUserRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/ecr_terms_conditions"
                  element={
                    <ProtectedRoute>
                      <VerifiedUserRoute>
                        <MasterCopyTermsConditionsPage
                          theme={theme as string}
                        />
                      </VerifiedUserRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/revoke_licenses"
                  element={
                    <AdminRoute>
                      <RevokeLicensePage
                        theme={theme as string}
                        toggleTheme={themeToggler as () => void}
                      />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/verify_artists"
                  element={
                    <AdminRoute>
                      <VerifyArtistsPage
                        theme={theme as string}
                        toggleTheme={themeToggler as () => void}
                      />
                    </AdminRoute>
                  }
                />
                <Route path='/privacy_policy'
                element={<PrivacyPolicyPage/>}
                />
              </Routes>
            </Router>
          </ThemeProvider>
        )}
      </AuthProvider>
    </>
  );
}

export default App;